import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
} from 'react';

import { cn } from '@/shared/lib/cn';

interface SwitchOwnProps {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
}

export type SwitchProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof SwitchOwnProps | 'onChange' | 'type' | 'role'
> &
  SwitchOwnProps;

export const Switch = forwardRef(function Switch(
  { checked, onCheckedChange, className, disabled, ...props }: SwitchProps,
  ref: ForwardedRef<HTMLButtonElement>,
): ReactElement {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onCheckedChange(!checked);
      }}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
        'transition-colors duration-200 ease-out-expo',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-muted',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none block h-5 w-5 rounded-full bg-background shadow-soft-1',
          'transition-transform duration-200 ease-out-expo',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
});
